import React, { useMemo } from 'react';
import { Box, Button, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  chip: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  summaryLabel: {
    color: '#8d93c9',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.55,
    marginBottom: 2,
  },
  summaryValue: {
    color: '#f1f3ff',
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.35,
  },
  section: {
    marginBottom: 14,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8,
  },
  sectionTitle: {
    color: '#e5e7ff',
    fontSize: 14,
    fontWeight: 900,
  },
  sectionMeta: {
    color: '#9ea4d8',
    fontSize: 11,
    lineHeight: 1.35,
  },
  actionButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    color: '#d9defa',
    borderColor: 'rgba(255,255,255,0.18)',
    padding: '4px 10px',
    minWidth: 'auto',
  },
  row: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.42)',
      background: 'rgba(16, 24, 50, 0.92)',
    },
  },
  rowTitle: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  rowSubtitle: {
    color: '#c9cff8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 4,
    wordBreak: 'break-word',
  },
  rowMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 6,
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  tag: {
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    color: '#d7dcff',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    lineHeight: 1.2,
  },
  insightCard: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.72)',
    padding: '10px 12px',
    marginBottom: 12,
  },
  insightTitle: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 900,
    marginBottom: 8,
  },
  insightItem: {
    color: '#c9cff8',
    fontSize: 12,
    lineHeight: 1.45,
    marginBottom: 6,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 10,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function compactDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'No timestamp';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

function formatConviction(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}% conviction`;
}

function uniqueStrings(values, limit = 4) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const v = String(value || '').trim();
    const key = v.toLowerCase();
    if (!v || seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });
  return out.slice(0, limit);
}

function getDateMs(value) {
  const ts = new Date(String(value || '').trim()).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function hoursAgo(value) {
  const ts = getDateMs(value);
  if (!ts) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - ts) / (1000 * 60 * 60));
}

function formatRelativeWindow(value) {
  const hours = hoursAgo(value);
  if (!Number.isFinite(hours)) return 'No recent timestamp';
  if (hours < 1) return 'Updated <1h ago';
  if (hours < 24) return `Updated ${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `Updated ${days}d ago`;
}

function summarizeConvictionTrend(history, currentConviction) {
  const points = Array.isArray(history) ? history : [];
  const normalized = points
    .map((item) => ({
      ts: getDateMs(item && item.ts),
      conviction: Number(item && item.conviction),
    }))
    .filter((item) => item.ts && Number.isFinite(item.conviction))
    .sort((a, b) => a.ts - b.ts);
  const current = Number(currentConviction);
  if (!normalized.length || !Number.isFinite(current)) return 'Conviction steady';
  const last = normalized[normalized.length - 1];
  const prev = normalized.length > 1 ? normalized[normalized.length - 2] : null;
  const baseline = prev && Number.isFinite(prev.conviction) ? prev.conviction : last.conviction;
  const delta = current - baseline;
  if (delta >= 0.06) return 'Conviction rising';
  if (delta <= -0.06) return 'Conviction cooling';
  return 'Conviction steady';
}

function buildStoryReasonTags(row, currentAddress, storyTypeCounts) {
  const tags = [];
  const directCandidates = [
    row && row.primary_token,
    row && row.meta && row.meta.token,
    row && row.meta && row.meta.token_address,
    row && row.meta && row.meta.contract,
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
  const current = String(currentAddress || '').trim().toLowerCase();
  if (current && directCandidates.includes(current)) tags.push('Direct token mention');
  if (Number(row && row.score) >= 0.75) tags.push('High-score event');
  const hours = hoursAgo(row && row.ts_created);
  if (hours <= 24) tags.push('Fresh <24h');
  const storyType = String((row && row.story_type) || '').trim();
  if (storyType && Number(storyTypeCounts.get(storyType) || 0) > 1) tags.push('Narrative repeat');
  if (String((row && row.subtitle) || '').match(/0x[0-9a-fA-F]{40}/)) tags.push('Counterparty mention');
  return uniqueStrings(tags, 3);
}

function buildThesisReasonTags(row) {
  const tags = [];
  const status = String((row && row.status) || '').trim();
  if (status) tags.push(status.charAt(0).toUpperCase() + status.slice(1));
  const storyCount = Array.isArray(row && row.contributing_story_ids) ? row.contributing_story_ids.length : 0;
  if (storyCount > 0) tags.push(`${storyCount} linked stor${storyCount === 1 ? 'y' : 'ies'}`);
  const riskFactors = Array.isArray(row && row.risk_factors) ? row.risk_factors : [];
  if (riskFactors.length) tags.push(`${riskFactors.length} risk factor${riskFactors.length === 1 ? '' : 's'}`);
  tags.push(summarizeConvictionTrend(row && row.conviction_history, row && row.conviction));
  return uniqueStrings(tags, 4);
}

function deriveNarrativeState(address, stories, theses) {
  const storyList = Array.isArray(stories && stories.stories) ? stories.stories : [];
  const thesisList = Array.isArray(theses && theses.theses) ? theses.theses : [];
  const storyTypeCounts = new Map();
  storyList.forEach((row) => {
    const key = String((row && row.story_type) || '').trim();
    if (!key) return;
    storyTypeCounts.set(key, Number(storyTypeCounts.get(key) || 0) + 1);
  });
  const rankedStories = storyList
    .map((row) => {
      const hours = hoursAgo(row && row.ts_created);
      const score = Number(row && row.score);
      const scoreWeight = Number.isFinite(score) ? score * 4 : 0;
      const recencyWeight = hours <= 24 ? 2 : hours <= 72 ? 1 : 0;
      const repeatWeight = Number(storyTypeCounts.get(String((row && row.story_type) || '').trim()) || 0) > 1 ? 0.8 : 0;
      const directCandidates = [row && row.primary_token, row && row.meta && row.meta.token, row && row.meta && row.meta.token_address, row && row.meta && row.meta.contract]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);
      const directWeight = String(address || '').trim().toLowerCase() && directCandidates.includes(String(address || '').trim().toLowerCase()) ? 1.5 : 0;
      return {
        ...row,
        reasonTags: buildStoryReasonTags(row, address, storyTypeCounts),
        rankScore: scoreWeight + recencyWeight + repeatWeight + directWeight,
      };
    })
    .sort((a, b) => b.rankScore - a.rankScore || getDateMs(b.ts_created) - getDateMs(a.ts_created));
  const rankedTheses = thesisList
    .map((row) => {
      const conviction = Number(row && row.conviction);
      const storyCount = Array.isArray(row && row.contributing_story_ids) ? row.contributing_story_ids.length : 0;
      const hours = hoursAgo(row && (row.ts_updated || row.ts_created));
      const status = String((row && row.status) || '').trim().toLowerCase();
      const rankScore = (Number.isFinite(conviction) ? conviction * 4 : 0)
        + (storyCount > 0 ? Math.min(2, storyCount * 0.35) : 0)
        + (hours <= 24 ? 1.5 : hours <= 72 ? 0.75 : 0)
        + (status === 'confirmed' ? 1 : status === 'active' ? 0.5 : 0);
      return {
        ...row,
        reasonTags: buildThesisReasonTags(row),
        convictionTrend: summarizeConvictionTrend(row && row.conviction_history, row && row.conviction),
        rankScore,
      };
    })
    .sort((a, b) => b.rankScore - a.rankScore || getDateMs(b.ts_updated || b.ts_created) - getDateMs(a.ts_updated || a.ts_created));
  const recentStoryCount24h = rankedStories.filter((row) => hoursAgo(row && row.ts_created) <= 24).length;
  const recentThesisCount72h = rankedTheses.filter((row) => hoursAgo(row && (row.ts_updated || row.ts_created)) <= 72).length;
  const allRiskFactors = uniqueStrings(rankedTheses.flatMap((row) => Array.isArray(row && row.risk_factors) ? row.risk_factors : []), 4);
  const repeatedNarratives = [...storyTypeCounts.entries()]
    .filter(([, count]) => Number(count) > 1)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 2)
    .map(([type, count]) => `${type} repeating (${count})`);
  const recentInsights = uniqueStrings([
    recentStoryCount24h ? `${recentStoryCount24h} high-signal stor${recentStoryCount24h === 1 ? 'y' : 'ies'} in the last 24h` : '',
    recentThesisCount72h ? `${recentThesisCount72h} thesis update${recentThesisCount72h === 1 ? '' : 's'} in the last 72h` : '',
    repeatedNarratives[0] || '',
    allRiskFactors.length ? `Top risk factors: ${allRiskFactors.join(', ')}` : '',
  ], 4);
  return {
    storyRows: rankedStories.slice(0, 3),
    thesisRows: rankedTheses.slice(0, 3),
    recentInsights,
    topThemes: [...storyTypeCounts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 2)
      .map(([type]) => type),
    latestStoryTs: rankedStories[0] && rankedStories[0].ts_created ? rankedStories[0].ts_created : '',
    latestThesisTs: rankedTheses[0] && (rankedTheses[0].ts_updated || rankedTheses[0].ts_created) ? (rankedTheses[0].ts_updated || rankedTheses[0].ts_created) : '',
    storyCount: storyList.length,
    thesisCount: thesisList.length,
  };
}

function buildTokenContext(address, metadata) {
  const tokenAddress = String(address || '').trim();
  const tokenLabel = metadata && (metadata.symbol || metadata.name)
    ? `${metadata.symbol || metadata.name}${metadata && metadata.name && metadata.symbol ? ` • ${metadata.name}` : ''}`
    : tokenAddress;
  const params = new URLSearchParams();
  if (tokenAddress) params.set('tokenAddress', tokenAddress);
  return {
    search: params.toString() ? `?${params.toString()}` : '',
    state: {
      tokenAddress,
      tokenLabel,
      tokenSymbol: metadata && metadata.symbol ? metadata.symbol : '',
      from: `/token/${tokenAddress}`,
    },
  };
 }

export default function NarrativePanel({ address, metadata, stories, theses, loading, error }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const tokenContext = buildTokenContext(address, metadata);
  const narrative = useMemo(() => deriveNarrativeState(address, stories, theses), [address, stories, theses]);
  const storyRows = narrative.storyRows;
  const thesisRows = narrative.thesisRows;

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Narrative Intelligence</Typography>
      <Divider className={classes.divider} />

      <Box className={classes.summaryRow}>
        <Box className={classes.chip}>
          <Typography className={classes.summaryLabel}>Stories</Typography>
          <Typography className={classes.summaryValue}>{narrative.storyCount}</Typography>
        </Box>
        <Box className={classes.chip}>
          <Typography className={classes.summaryLabel}>Theses</Typography>
          <Typography className={classes.summaryValue}>{narrative.thesisCount}</Typography>
        </Box>
        {narrative.latestStoryTs ? (
          <Box className={classes.chip}>
            <Typography className={classes.summaryLabel}>Latest story</Typography>
            <Typography className={classes.summaryValue}>{formatRelativeWindow(narrative.latestStoryTs)}</Typography>
          </Box>
        ) : null}
        {narrative.latestThesisTs ? (
          <Box className={classes.chip}>
            <Typography className={classes.summaryLabel}>Latest thesis</Typography>
            <Typography className={classes.summaryValue}>{formatRelativeWindow(narrative.latestThesisTs)}</Typography>
          </Box>
        ) : null}
        {narrative.topThemes.map((theme) => (
          <Box key={theme} className={classes.chip}>{theme}</Box>
        ))}
      </Box>

      {narrative.recentInsights.length ? (
        <Box className={classes.insightCard}>
          <Typography className={classes.insightTitle}>What Changed Recently</Typography>
          {narrative.recentInsights.map((item) => (
            <Typography key={item} className={classes.insightItem}>{item}</Typography>
          ))}
        </Box>
      ) : null}

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography className={classes.sectionTitle}>Ranked Story Evidence</Typography>
            <Typography className={classes.sectionMeta}>Recent high-signal stories ranked by score, recency, and direct token relevance.</Typography>
          </Box>
          <Button className={classes.actionButton} variant="outlined" onClick={() => navigate(`/stories${tokenContext.search}`, { state: tokenContext.state })}>View all</Button>
        </Box>
        {storyRows.length ? storyRows.map((row) => (
          <Box key={row.id || row.title} className={classes.row} onClick={() => navigate(`/stories${tokenContext.search ? `${tokenContext.search}&` : '?'}storyId=${encodeURIComponent(String(row.id || ''))}`, { state: tokenContext.state })}>
            <Typography className={classes.rowTitle}>{row.title || 'Untitled story'}</Typography>
            {row.subtitle ? <Typography className={classes.rowSubtitle}>{String(row.subtitle).slice(0, 180)}</Typography> : null}
            <Typography className={classes.rowMeta}>
              {(row.story_type || 'Story')} • {compactDate(row.ts_created)} • Score {formatScore(row.score)}
            </Typography>
            {Array.isArray(row.reasonTags) && row.reasonTags.length ? (
              <Box className={classes.tagsRow}>
                {row.reasonTags.map((tag) => (
                  <Box key={tag} className={classes.tag}>{tag}</Box>
                ))}
              </Box>
            ) : null}
          </Box>
        )) : <Typography className={classes.empty}>{loading ? 'Loading token-linked stories…' : 'No token-linked stories yet.'}</Typography>}
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography className={classes.sectionTitle}>Ranked Thesis Signals</Typography>
            <Typography className={classes.sectionMeta}>Active token theses ranked by conviction, freshness, and linked evidence depth.</Typography>
          </Box>
          <Button className={classes.actionButton} variant="outlined" onClick={() => navigate(`/thesis${tokenContext.search}`, { state: tokenContext.state })}>View all</Button>
        </Box>
        {thesisRows.length ? thesisRows.map((row) => (
          <Box key={row.id || row.entity_address || row.entity_symbol} className={classes.row} onClick={() => navigate(`/thesis${tokenContext.search ? `${tokenContext.search}&` : '?'}thesisId=${encodeURIComponent(String(row.id || ''))}`, { state: tokenContext.state })}>
            <Typography className={classes.rowTitle}>{row.entity_symbol || row.entity_name || 'Unnamed thesis target'}</Typography>
            {row.thesis_text ? <Typography className={classes.rowSubtitle}>{String(row.thesis_text).slice(0, 180)}</Typography> : null}
            <Typography className={classes.rowMeta}>
              {(row.direction || 'View').toUpperCase()} • {formatConviction(row.conviction)} • {compactDate(row.ts_updated || row.ts_created)} • {row.convictionTrend}
            </Typography>
            {Array.isArray(row.reasonTags) && row.reasonTags.length ? (
              <Box className={classes.tagsRow}>
                {row.reasonTags.map((tag) => (
                  <Box key={tag} className={classes.tag}>{tag}</Box>
                ))}
                {Array.isArray(row.risk_factors) ? row.risk_factors.slice(0, 2).map((risk) => (
                  <Box key={risk} className={classes.tag}>{risk}</Box>
                )) : null}
              </Box>
            ) : null}
          </Box>
        )) : <Typography className={classes.empty}>{loading ? 'Loading token-linked theses…' : 'No token-linked theses yet.'}</Typography>}
      </Box>

      <Typography className={classes.footer}>
        {loading ? 'Refreshing narrative inputs…' : 'Narrative intelligence now prioritizes what changed recently, which theses are strengthening, and which evidence threads deserve the next click.'}
      </Typography>
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
